import React, { useContext, useEffect, useMemo } from "react";
import * as _ from "lodash-es";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import BetaBadge from "../../../shared/BetaBadge/BetaBadge";
import { SettingsCardContext, SettingsSectionContext, useSettingsPage } from "../SettingsPage/SettingsPageContext";

type Props = {
    /** Translation key of the label. */
    title: string;
    /** Translation key of the help text under the label. */
    detail?: string;
    toggleFn?: (e: any, subProp?: string) => void;
    isActive?: boolean;
    showDisableConfirm?: boolean;
    showEnableConfirm?: boolean;
    /** Supplier model key the row edits. "" for rows that only host a component. */
    name: string;
    /** Replaces the toggle with any control (select, input, button, uploader...). */
    component?: any;
    /** Legacy: content shown under the row while the toggle is on. Prefer `children`. */
    toggledOnComponent?: any;
    disabled?: boolean;
    optionClassName?: string;
    betaBadge?: boolean;
    /** Nested rows, rendered in an indented group only while the toggle is on. */
    children?: React.ReactNode;
    /** Show the nested group regardless of the toggle (for rows whose children are always relevant). */
    alwaysShowChildren?: boolean;
    /** Key compared against the last saved model for the "Edited" mark; defaults to `name`. */
    editedKey?: string;
    /** Why the row is disabled; usually a SettingsDependencyChip. Shown next to the control. */
    disabledReason?: React.ReactNode;
    /** Extra controls placed before the toggle (an "Edit ..." button, a count, a chip). */
    beforeControl?: React.ReactNode;
    /** The component spans the full row width (text areas, previews). */
    wide?: boolean;
    /** Override the DOM id (defaults to "row-<name>" or "row-<title>"). */
    id?: string;
};

/**
 * One setting: label and help on the left, control on the right, optional nested group below.
 * Registers itself with the page so the sticky header can search it and mark it as edited.
 */
export const SettingsRow: React.FC<Props> = (props) => {
    const page = useSettingsPage();
    const section = useContext(SettingsSectionContext);
    const card = useContext(SettingsCardContext);

    const rowId = props.id || `row-${props.name || _.kebabCase(props.title)}`;
    const editedKey = props.editedKey || props.name;
    const edited = editedKey ? page.isEdited(editedKey) : false;
    const highlighted = page.highlightedRowId === rowId;

    const sectionId = section ? section.id : "";
    const cardId = card ? card.id : "";
    const { registerRow } = page;

    useEffect(() => {
        return registerRow({ id: rowId, name: props.name, titleKey: props.title, detailKey: props.detail, sectionId, cardId });
    }, [registerRow, rowId, props.name, props.title, props.detail, sectionId, cardId]);

    const showNested = useMemo(() => {
        const hasNested = !_.isNil(props.children) || !_.isNil(props.toggledOnComponent);

        return hasNested && (props.alwaysShowChildren || !!props.isActive);
    }, [props.children, props.toggledOnComponent, props.alwaysShowChildren, props.isActive]);

    const className = [
        "SettingsRow",
        edited ? "edited" : "",
        props.disabled ? "disabled" : "",
        highlighted ? "highlighted" : "",
        props.wide ? "wide" : "",
        props.component && !props.wide ? "has-component" : "",
        props.optionClassName || "",
    ]
        .filter(Boolean)
        .join(" ");

    const control = props.component ? (
        props.component
    ) : props.toggleFn ? (
        <OwToggle
            size={"small"}
            active={!!props.isActive}
            name={props.name}
            fn={props.toggleFn}
            label={""}
            showDisableConfirm={props.showDisableConfirm}
            showEnableConfirm={props.showEnableConfirm}
            disabled={props.disabled}
        />
    ) : null;

    return (
        <div className={className} id={rowId} data-testid={rowId} data-name={props.name}>
            <div className={"SettingsRow-text"}>
                <div className={"SettingsRow-label"}>
                    <OwLabel title={props.title} noBottomMargin={true} />
                    {props.betaBadge ? <BetaBadge className={"ms-2"} /> : null}
                </div>
                {props.detail ? (
                    <div className={"SettingsRow-help"}>
                        <OwLabel title={props.detail} noBottomMargin={true} preserveNewlines={true} />
                    </div>
                ) : null}
            </div>

            <div className={"SettingsRow-control"}>
                {edited ? (
                    <span className={"SettingsChip brand"} data-testid={`${rowId}-edited`}>
                        <OwLabel title={"edited"} noBottomMargin={true} />
                    </span>
                ) : null}
                {props.disabledReason}
                {props.beforeControl}
                {control}
            </div>

            {showNested ? (
                <div className={"SettingsRow-nested"}>
                    {props.toggledOnComponent}
                    {props.children}
                </div>
            ) : null}
        </div>
    );
};

export default SettingsRow;
